/**
 * WalletCounterLabel - Shows accumulated bet amount above the wallet bill stack
 *
 * Callout style: solid black rectangle + triangle pointer on the right edge
 * pointing toward the cash bills. Oswald regular (400), white text.
 * Uses TSS with triangle node type.
 */

import * as THREE from 'three';
import { TSS } from '../../tss/TSS';
import { UINodeConfig } from '../../tss/types';

/** Format amount without $ prefix: 200, 1.5K, 30M */
function formatAmount(value: number): string {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return `${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  } else if (value >= 1000) {
    const thousands = value / 1000;
    return `${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }
  return `${value}`;
}

const ARROW_WIDTH = 22;
const ARROW_HEIGHT = 16; // full height (was 8 half-height in old code)
const FONT_SIZE = 34;
const LABEL_WORLD_HEIGHT = 0.4;
const LABEL_POSITION = { x: -0.8, y: 1.6, z: -0.2 };

export class WalletCounterLabel {
  private component: TSS;
  private lastAmount = 0;

  constructor(parent: THREE.Object3D) {
    this.component = new TSS(
      this.buildTree(''),
      {
        worldHeight: LABEL_WORLD_HEIGHT,
        mode: 'sprite',
        resolution: 1,
        depthTest: false,
        depthWrite: false,
        // Always render on top — the bet total is the player's primary
        // confirmation that bills are accumulating. Sits above the lock
        // overlay (renderOrder 100) and any other 3D HUD elements.
        renderOrder: 1000,
        spriteCenter: { x: 1.0, y: 0.5 },
      }
    );

    this.component.object3D.position.set(LABEL_POSITION.x, LABEL_POSITION.y, LABEL_POSITION.z);
    this.component.object3D.visible = false;
    parent.add(this.component.object3D);
  }

  private buildTree(text: string): UINodeConfig {
    return {
      tag: 'root',
      style: {
        direction: 'row',
        alignItems: 'center',
        gap: 0,
      },
      children: [
        {
          tag: 'body',
          style: {
            padding: [8, 14, 8, 14],
            backgroundColor: '#000000',
          },
          children: [
            {
              tag: 'amount',
              text,
              style: {
                fontSize: FONT_SIZE,
                fontFamily: 'Oswald, sans-serif',
                fontWeight: 'normal',
                color: '#FFFFFF',
                textAlign: 'center',
                minWidth: 80,
              },
            },
          ],
        },
        {
          tag: 'arrow',
          triangle: {
            width: ARROW_WIDTH,
            height: ARROW_HEIGHT,
            direction: 'right',
            fill: '#000000',
          },
        },
      ],
    };
  }

  setAmount(amount: number): void {
    if (amount === this.lastAmount) return;
    this.lastAmount = amount;
    this.component.setTree(this.buildTree(formatAmount(amount)));
    this.component.render();
  }

  setVisible(visible: boolean): void {
    this.component.setVisible(visible);
  }

  dispose(): void {
    this.component.dispose();
  }
}
